import React, { useState, useRef, useEffect, useMemo, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { updateFamilyMember, fetchFamilyMemberById } from "../../service/FamilyMemberApi";
import { fetchFamilyById } from "../../service/FamilyApi";
import { useForm } from "react-hook-form";
import { Helmet } from "react-helmet-async";
import { Navbar, Footer, Breadcrumbs } from "../../components/shared";
import { Loading } from "../../components/shared";
import { businessNames, relationShips } from '../general/DropDownValuesConfig';
import { getCroppedImg, checkBase64Size, useCheckAccess, getYearOptions, maskAadhar } from "../../utils/Utilities";
import { translateText } from "../../service/UtilApi"; // Import translation function
import { FileUpload } from '../file/gcp/FileUpload';
import Cropper from 'react-easy-crop';
import '../general/crop.css';
import { useSelector } from "react-redux";

function FamilyMemberUpdate() {
  useCheckAccess('FamilyRW'); // Check if the user is logged in (if no user data, redirect to login)
  const { id } = useParams();
  const navigate = useNavigate();
  const user = useSelector((state) => state.user);
  const [familyData, setFamilyData] = useState(null);
  const [familyMemberData, setFamilyMemberData] = useState(null);
  const { trigger, clearErrors, register, handleSubmit, formState: { errors, isValid, isSubmitting }, setValue } = useForm({ mode: "all" });
  const [loading, setLoading] = React.useState(false);
  const [imageFile, setImageFile] = useState(null); // Raw image file
  const [errorMessage, setErrorMessage] = useState('');
  const [error, setError] = useState(null);
  const dateInputRef = useRef(null);
  const [selectedYear, setSelectedYear] = useState("");
  const [croppedImageBase64, setCroppedImageBase64] = useState(null);
  const [uploadMode, setUploadMode] = useState('both'); // 'none', 'both', 'applicationOnly'
  const [showUploadConfirmation, setShowUploadConfirmation] = useState(false);
  const [currentSelectedFile, setCurrentSelectedFile] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [isAspectRatioLocked, setIsAspectRatioLocked] = useState(true);
  const [nameData, setNameData] = useState({
    tempFirstName: "",
    tempLastName: "",
    firstName: "",
    lastName: "",
  });
  // Fetch family data
  useEffect(() => {
    const fetchFamilyMemberData = async () => {
      try {
        if (id) {
          setLoading(true);
          const data = await fetchFamilyMemberById(id);
          setFamilyMemberData(data); // Set the fetched data to state
          const dataFamliy = await fetchFamilyById(data.familyMember.familyId); // Await the fetchFamilyById function
          setFamilyData(dataFamliy); // Set the family data in state
          // Ensure this part is inside the effect where `familyMemberData` is available
          setNameData({
            firstName: data.familyMember.firstName || "",
            lastName: data.familyMember.lastName || "",
            tempLastName: data.familyMember.lastName || "",
            tempFirstName: data.familyMember.firstName || "",
          });
          // Pre-populate the form with fetched data when editing
          setValue("firstName", data.familyMember.firstName || "");
          setValue("lastName", data.familyMember.lastName || "");
          setValue("businessName", data.familyMember.businessName || "");
          setValue("dob", data.familyMember.dob || "");
          setValue("mobile", data.familyMember.mobile || "");
          setValue("mobileSmsStatus", data.familyMember.mobileSmsStatus || "");
          setValue("mobile2", data.familyMember.mobile2 || "");
          setValue("email", data.familyMember.email || "");
          setValue("aadhar", data.familyMember.aadhar || "");
          setValue("status", data.familyMember.status || "");
          setValue("parentId", data.familyMember.parentId || data.familyMember.aadhar);
          setValue("id", data.familyMember.id); // ✅ Set ID
          setValue("familyId", data.familyMember.familyId); // ✅ Set familyId
          await trigger();
          setLoading(false);
        }
      } catch (error) {
        setError("Error fetching family member data");
      }
    };

    fetchFamilyMemberData();

  }, [id, setValue, setNameData, trigger]);


  const handleYearChange = (e) => {
    const year = e.target.value;
    setSelectedYear(year);

    const dateString = `${year}-06-01`;

    // Update both DOM and react-hook-form state
    if (dateInputRef.current) {
      dateInputRef.current.value = dateString;
    }
    setValue("dob", dateString, { shouldValidate: true });
  };

  const handleNameChange = (field, value) => {
    setNameData((prevData) => ({
      ...prevData,
      [field]: value, // Dynamically set the correct field
    }));
  };

  // Translate the first name to Tamil
  const translateNameToTamil = async () => {

    try {
      if (nameData.tempFirstName) {

        const translatedFirstName = await translateText(nameData.tempFirstName);
        setNameData((prevData) => ({
          ...prevData,
          firstName: translatedFirstName + " ",
        }));
      }
      if (nameData.tempLastName) {
        const translatedLastName = await translateText(nameData.tempLastName);
        setNameData((prevData) => ({
          ...prevData,
          lastName: translatedLastName + " ",
        }));
      }
    } catch (error) {
      console.error("Error translating name:", error);
    }
  };


  // Handle form submission (API update)
  const onSubmit = async (data) => {
    if (loading) {
      return;  // Prevent form submission if loading is true
    }
    if (!nameData.firstName || nameData.firstName.length < 3) {
      alert("பெயர் சரியாக உள்ளிடப்படவில்லை.");
      return;
    }

    if (
      data.maskedAadhar &&
      (data.maskedAadhar.startsWith("TMP") || data.maskedAadhar.startsWith("00")) &&
      data.status === "active"
    ) {
      data.status = "draft";
      alert("ஆதார் விவரங்கள் முடிவடையாததால், நிலையை 'active' மாற்ற முடியாது");
      return;
    }
    let gcsFamilyMemberPhotoPath = null;
    setLoading(true);

    try {
      if (
        nameData.firstName.trim() !== familyMemberData.familyMember.firstName ||
        nameData.lastName.trim() !== familyMemberData.familyMember.lastName ||
        data.businessName !== familyMemberData.familyMember.businessName ||
        data.dob !== familyMemberData.familyMember.dob ||
        data.mobile !== familyMemberData.familyMember.mobile ||
        data.mobileSmsStatus !== familyMemberData.familyMember.mobileSmsStatus ||
        data.mobile2 !== familyMemberData.familyMember.mobile2 ||
        data.email !== familyMemberData.familyMember.email ||
        data.aadhar !== familyMemberData.familyMember.aadhar ||
        data.status !== familyMemberData.familyMember.status ||
        data.relationship !== familyMemberData.familyMember.relationship ||
        data.parentId !== familyMemberData.familyMember.parentId ||
        croppedAreaPixels
      ) {
        if (croppedAreaPixels) {
          try {
            // Generate the blob from the original imageURL and the croppedAreaPixels
            const blob = await getCroppedImg(imageURL, croppedAreaPixels);

            // Convert blob to Base64 to check its size before proceeding
            const reader = new FileReader();
            reader.readAsDataURL(blob);
            await new Promise(resolve => reader.onloadend = resolve); // Wait for reader to finish
            const base64CroppedForSizeCheck = reader.result;

            if (!checkBase64Size(base64CroppedForSizeCheck)) {
              // Replaced alert
              setErrorMessage("Cropped image size should be less than 900 KB.");
              setLoading(false);
              return;
            }
            // Create a File object from the blob for upload
            const croppedFile = new File([blob], `${familyMemberData.familyMember.uid}.jpg`, { type: 'image/jpeg' });
            gcsFamilyMemberPhotoPath = await FileUpload(
              croppedFile,
              'family_member',
              'photo',
              familyMemberData.familyMember.uid
            );
            if (gcsFamilyMemberPhotoPath === false) {
              return;
            }
          } catch (error) {
            console.error("Error uploading photo:", error);
            alert("Error uploading photo:", error);
            setLoading(false);
            return;
          }
        }
        const payload = {
          ...data,
          firstName: nameData.firstName.trim(),
          lastName: nameData.lastName.trim(),
          photoPath: gcsFamilyMemberPhotoPath,
          uid: familyMemberData.familyMember.uid,
        };
        const success = await updateFamilyMember(payload);
        if (success) {
          navigate(`/family/member-view/${id}`);
        }
      }

    } catch (error) {
      console.error("Error updating family member:", error);
    } finally {
      setLoading(false);  // Set loading back to false after completion
    }
  };
  const handleChange = async (fieldName) => {
    await trigger(fieldName);  // Trigger field validation
    clearErrors(fieldName);  // Clear error if valid
  };


  ////

  // Memoized URL for the selected image file to be used by ReactCropper
  const imageURL = useMemo(() => {
    if (imageFile) {
      return URL.createObjectURL(imageFile);
    }
    return null;
  }, [imageFile]);
  // Handle file input change for image upload - now triggers confirmation
  const handleImageSelect = useCallback((e) => {
    const file = e.target.files[0];
    if (file) {
      const MAX_FILE_SIZE = 1000000; // 1 MB
      if (file.size > MAX_FILE_SIZE) {
        setErrorMessage('Image size should be less than 1 MB.');
        e.target.value = ''; // Clear the file input
        setCurrentSelectedFile(null);
        setImageFile(null); // Clear any previously set imageFile
        setCrop({ x: 0, y: 0 });
        setZoom(1);
        setCroppedAreaPixels(null); // Clear any previously cropped image
        setCroppedImageBase64(null); // Clear any previously cropped image Base64
        setUploadMode('none');
      } else {
        setCurrentSelectedFile(file); // Store the selected file temporarily
        setShowUploadConfirmation(true); // Show confirmation dialog
        setErrorMessage('');
      }
    } else {
      // If input is cleared, reset all related states
      setCurrentSelectedFile(null);
      setImageFile(null);
      setCrop({ x: 0, y: 0 });
      setZoom(1);
      setCroppedAreaPixels(null);
      setCroppedImageBase64(null); // Clear any previously cropped image Base64
      setErrorMessage('');
      setUploadMode('none');
      setShowUploadConfirmation(false);
    }
  }, []);

  // Handle user's choice from the confirmation dialog
  const handleConfirmationChoice = useCallback((choice) => {
    if (choice === 'both') {
      setImageFile(currentSelectedFile);
      setUploadMode('both');
    } else if (choice === 'applicationOnly') {
      setImageFile(currentSelectedFile);
      setCroppedAreaPixels(null);
      setCroppedImageBase64(null);
      setUploadMode('applicationOnly');
    }
    setShowUploadConfirmation(false);
    setCurrentSelectedFile(null);
  }, [currentSelectedFile]);

  const handleCancelUpload = useCallback(() => {
    // Reset all image-related states if user cancels
    setCurrentSelectedFile(null);
    setImageFile(null);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setCroppedAreaPixels(null);
    setCroppedImageBase64(null); // Clear any previously cropped image Base64
    setErrorMessage('');
    setUploadMode('none');
    setShowUploadConfirmation(false);
    const fileInput = document.getElementById('applicationFormFile');
    if (fileInput) {
      fileInput.value = '';
    }
  }, []);
  const onCropComplete = useCallback((_, newCroppedAreaPixels) => {
    setCroppedAreaPixels(newCroppedAreaPixels);
  }, []);


  const handleCrop = useCallback(async () => {
    if (!imageURL || !croppedAreaPixels) {
      setErrorMessage('Please select an image and define a crop area first.');
      return;
    }
    if (Object.values(croppedAreaPixels).some(isNaN)) {
      setErrorMessage('Invalid crop area detected. Please try re-cropping.');
      console.error('CroppedAreaPixels contains NaN values:', croppedAreaPixels);
      return;
    }
    try {
      const blob = await getCroppedImg(imageURL, croppedAreaPixels);
      const reader = new FileReader();
      reader.readAsDataURL(blob);
      reader.onloadend = () => {
        const base64data = reader.result;
        if (!checkBase64Size(base64data)) {
          setErrorMessage('Cropped image size should be less than 900 KB.');
          setCroppedImageBase64(null);
        } else {
          setCroppedImageBase64(base64data); // Store the Base64 string for preview
          setErrorMessage('');
        }
      };
      reader.onerror = (error) => {
        console.error('FileReader error:', error);
        setErrorMessage('Failed to read cropped image data.');
      };
    } catch (error) {
      console.error('Crop failed:', error);
      setErrorMessage('படத்தை வெட்ட முடியவில்லை');
    }
  }, [imageURL, croppedAreaPixels]);



  ////


  if (error) {
    return <div>Error: {error}</div>; // Display error message
  }
  // If data is not available, show loading
  if (!familyMemberData || loading) {
    return (
      <>
        <Navbar />
        <div className="general_parent">
          <div className="general_container">
            <div className="general_body">
              <Breadcrumbs /><br />
              <Loading />
            </div>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Navbar />
      <Helmet>
        <title>Update Family Member</title>
        <link rel="canonical" href={`/family/member-update`} />
      </Helmet>

      <div className="general_parent">
        <div className="general_container">
          <div className="general_body">
            <Breadcrumbs /><br />
            <h3>குடும்ப உறுப்பினர் விவரங்கள்</h3>
            <br />
            <form onSubmit={handleSubmit(onSubmit)} autoComplete="off">
              <input
                type="hidden"
                {...register("id")} // Register the id field
              />
              <input
                type="hidden"
                {...register("aadhar")} // Register the id field
              />

              <input
                type="hidden"
                {...register("familyId")} // Register the id field
              />


              <div className="details-card">
                <div className="detail-item">
                  <span className="detail-label"><strong>குடும்ப எண் :</strong></span>
                  <span className="detail-value">{familyMemberData.familyMember.familyId}</span>
                </div>

                {/* Aadhar Number */}
                <div className="detail-item">
                  <span className="detail-label"><strong>ஆதார் எண் :</strong></span>
                  <span>{maskAadhar(familyMemberData.familyMember.maskedAadhar) || "இல்லை"}</span>
                </div>

                {/* First Name */}
                <div className="auth_element">
                  <label className="auth_label">பெயர்(English)</label>
                  <input
                    type="text"
                    id="tempFirstName"
                    value={nameData.tempFirstName || ""}
                    {...register("tempFirstName", {
                      required: "பெயர் தேவை",
                      pattern: { value: /^[A-Za-z\u0B80-\u0BFF\s.]*$/, message: "எழுத்துக்கள், இடைவெளிகள் மற்றும் '.' மட்டும் அனுமதிக்கப்படும்" },
                      minLength: { value: 2, message: "குறைந்தது 2 எழுத்துகள் வேண்டும்" },
                    })}
                    className="auth_input"
                    placeholder="பொன்னாச்சி"
                    onChange={(e) => {
                      handleChange("tempFirstName");  // Trigger validation
                      handleNameChange("tempFirstName", e.target.value);  // Update state
                    }}
                  />
                  {errors.tempFirstName && <div className="auth_error">❗ {errors.tempFirstName.message}</div>}
                </div>

                <div className="auth_element">
                  <label className="auth_label">தந்தை/கணவர் பெயர்(English)</label>
                  <input
                    type="text"
                    id="tempLastName"
                    value={nameData.tempLastName || ""}
                    {...register("tempLastName", {
                      required: "பெயர் தேவை",
                      pattern: { value: /^[A-Za-z\u0B80-\u0BFF\s.]*$/, message: "எழுத்துக்கள், இடைவெளிகள் மற்றும் '.' மட்டும் அனுமதிக்கப்படும்" },
                      minLength: { value: 2, message: "குறைந்தது 2 எழுத்துகள் வேண்டும்" },
                    })}
                    className="auth_input"
                    placeholder="அம்மன்"
                    onChange={(e) => {
                      handleChange("tempLastName");  // Trigger validation
                      handleNameChange("tempLastName", e.target.value);  // Update state
                    }}
                  />
                  {errors.tempLastName && <div className="auth_error">❗ {errors.tempLastName.message}</div>}
                </div>
                <button
                  type="button"
                  onClick={translateNameToTamil}
                  disabled={loading}
                >
                  தமிழில் மொழிபெயர்க்கவும்
                </button>
                <div className="auth_element">
                  <label className="auth_label">பெயர்</label>
                  <input
                    type="text"
                    id="firstName"
                    value={nameData.firstName || ""}
                    className="auth_input"
                    placeholder="பொன்னாச்சி"
                    onChange={(e) => handleNameChange("firstName", e.target.value)} // Add onChange handler here
                  />
                  {errors.firstName && <div className="auth_error">❗ {errors.firstName.message}</div>}
                </div>

                <div className="auth_element">
                  <label className="auth_label">தந்தை/கணவர் பெயர்</label>
                  <input
                    type="text"
                    id="lastName"
                    value={nameData.lastName || ""}
                    className="auth_input"
                    placeholder="அம்மன்"
                    onChange={(e) => handleNameChange("lastName", e.target.value)}
                  />
                  {errors.lastName && <div className="auth_error">❗ {errors.lastName.message}</div>}
                </div>


                {/* Optional Business Name Field */}
                <div className="auth_element">
                  <label className="auth_label">வணிக பெயர் (தேவையில்லை)</label>
                  <select
                    id="businessName"
                    {...register("businessName")}
                    className="auth_select"
                    defaultValue={familyMemberData?.familyMember.businessName || ""}
                  >
                    <option value="">--Select Business--</option>
                    {businessNames.map((business) => (
                      <option key={business.value} value={business.value}>
                        {business.label}
                      </option>
                    ))}
                  </select>
                  {errors.businessName && <div className="auth_error">❗ {errors.businessName.message}</div>}
                </div>
                {/* Date of Birth */}
                <div className="auth_element">
                  <label className="auth_label">பிறந்த தேதி</label>

                  <div style={{ display: "flex", gap: "10px" }}>
                    {/* Year dropdown */}
                    <select
                      onChange={handleYearChange}
                      value={selectedYear}
                      className="auth_input"
                    >
                      <option value="">ஆண்டு தேர்வு செய்யவும்</option>
                      {getYearOptions(1940).map((year) => (
                        <option key={year} value={year}>{year}</option>
                      ))}
                    </select>

                    {/* Actual date input */}
                    <input
                      type="date"
                      id="dob"
                      ref={dateInputRef}
                      {...register("dob")}
                      className="auth_input"
                    />
                  </div>

                  {errors.dob && (
                    <div className="auth_error">❗ {errors.dob.message}</div>
                  )}
                </div>

                {/* Mobile Numbers */}
                <div className="auth_element">
                  <label className="auth_label">கைபேசி எண் (தேவையில்லை)</label>
                  <input
                    type="tel"
                    id="mobile"
                    {...register("mobile", {
                      pattern: {
                        value: /^\d{10}$/,
                        message: "சரியான கைபேசி எண்ணை உள்ளிடவும்",
                      },
                    })}
                    className="auth_input"
                    placeholder="+91-1234567890"
                  />
                  {errors.mobile && <div className="auth_error">❗ {errors.mobile.message}</div>}
                </div>
                {user?.roles?.includes('FamilyAdmin') &&
                  <div className="auth_element">
                    <label className="auth_label">கைபேசி SMS நிலை (தேவையில்லை)</label>
                    <input
                      type="text"
                      id="mobileSmsStatus"
                      {...register("mobileSmsStatus")}
                      className="auth_input"
                    />
                    {errors.mobileSmsStatus && <div className="auth_error">❗ {errors.mobileSmsStatus.message}</div>}
                  </div>
                }

                <div className="auth_element">
                  <label className="auth_label">கைபேசி எண் 2 (தேவையில்லை)</label>
                  <input
                    type="tel"
                    id="mobile2"
                    {...register("mobile2", {
                      pattern: {
                        value: /^\+91[-]?\d{3}[-]?\d{3}[-]?\d{4}$|^(?:\+?\d{1,3})?[-.\s]?(\(?\d{2,3}\)?)?[-.\s]?\d{3}[-.\s]?\d{4}$/,
                        message: "சரியான கைபேசி எண்ணை உள்ளிடவும்",
                      },
                    })}
                    className="auth_input"
                    placeholder="+91-1234567890"
                  />
                  {errors.mobile2 && <div className="auth_error">❗ {errors.mobile2.message}</div>}
                </div>

                {/* Email */}
                <div className="auth_element">
                  <label className="auth_label">மின்னஞ்சல்(தேவையில்லை)</label>
                  <input
                    type="email"
                    id="email"
                    {...register("email", {
                      pattern: {
                        value: /^[a-zA-Z0-9._%+-]+(?:[a-zA-Z0-9-]*[a-zA-Z0-9]+)*@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
                        message: "மின்னஞ்சல் செல்லுபடியாகாது",
                      },
                    })}
                    className="auth_input"
                    placeholder="ponnachiamman@gmail.com"
                  />
                  {errors.email && <div className="auth_error">❗ {errors.email.message}</div>}
                </div>
                <div className="auth_element">
                  <span className="auth_label"><strong>நிலை:</strong></span>
                  <div className="auth_radio">
                    <label className="auth_radio-label">
                      <input
                        type="radio"
                        value="active"
                        {...register("status", { required: "நிலை is required" })}
                      />
                      ✅ active (ஏற்றுக்கொள்ளப்பட்டது)
                    </label>
                    <label className="auth_radio-label">
                      <input
                        type="radio"
                        value="inactive"
                        {...register("status", { required: "நிலை is required" })}
                      />
                      ❌ inactive (நீக்கப்பட்டது)
                    </label>
                    <label className="auth_radio-label">
                      <input
                        type="radio"
                        value="draft"
                        {...register("status", { required: "நிலை is required" })}
                      />
                      📝 draft (குடிபாடு உறுதிப்படுத்தப்படவில்லை)
                    </label>
                    <label className="auth_radio-label">
                      <input
                        type="radio"
                        value="pending"
                        {...register("status", { required: "நிலை is required" })}
                      />
                      ⚠️⏳ pending (Applied by public)
                    </label>
                    {errors.status && <div className="auth_error">❗ {errors.status.message}</div>}
                  </div>
                </div>
                <div className="auth_element">
                  <label className="auth_label">குடும்ப உறவு</label>
                  <select
                    id="relationship"
                    {...register("relationship", { required: "உறவு தேர்ந்தெடுக்கவும்" })}
                    className="auth_select"
                    defaultValue={familyMemberData?.familyMember.relationship || ""}
                  >
                    <option value="">--Select Relationship--</option>
                    {relationShips.map((relation) => (
                      <option key={relation.value} value={relation.value}>
                        {relation.label}
                      </option>
                    ))}
                  </select>
                  {errors.relationship && <div className="auth_error">❗ {errors.relationship.message}</div>}
                </div>
                {/* Parent ID Dropdown */}

                {familyData?.family?.familyMembers && (
                  <div className="auth_element">
                    <label className="auth_label">Parent ID (Aadhaar ID)</label>
                    <select
                      id="parentId"
                      {...register("parentId")}
                      className="auth_select"
                    >
                      <option value="">--Select Parent ID--</option>
                      {familyData.family.familyMembers
                        .filter((member) => /^\d{12}$/.test(member.maskedAadhar)) // Only valid Aadhaar
                        .map((member) => (
                          <option key={member.aadhar} value={member.aadhar}>
                            {member.firstName} {member.lastName} ({maskAadhar(member.maskedAadhar)})
                          </option>
                        ))}
                    </select>
                    {errors.parentId && <div className="auth_error">❗ {errors.parentId.message}</div>}
                  </div>
                )}

                <div className="auth_element">
                  <label className="auth_label">Photo (Optional)</label>
                  <input
                    type="file"
                    id="applicationFormFile"
                    accept="image/*"
                    onChange={handleImageSelect}
                    className="auth_input"
                  />
                  {errorMessage && <div className="auth_error">{errorMessage}</div>}

                  {/* Show original image preview */}
                  {imageFile && (
                    <div className="auth_element">
                      <p className="text-gray-700 font-semibold mb-2">🖼️ Original Image Preview:</p>
                      <img
                        src={imageURL}
                        alt="Original Selected"
                        style={{ maxWidth: '100%', height: 'auto', border: '1px solid #ccc', padding: '4px' }}
                      />
                    </div>
                  )}
                </div>

                {showUploadConfirmation && (
                  <div className="confirmation-dialog">
                    <div className="flex justify-around gap-4">
                      <button
                        type="button"
                        onClick={() => handleConfirmationChoice('both')}
                      >
                        புகைப்படப் பகுதியைத் தேர்ந்தெடுக்கவும்
                      </button>
                    </div>
                    <button
                      type="button"
                      onClick={handleCancelUpload}
                      className="action-button cancel-button"
                    >
                      ரத்து செய்
                    </button>
                  </div>
                )}
                {/* Display Cropper only if image is uploaded AND uploadMode is 'both' */}
                {imageFile && uploadMode === 'both' && (
                  <div className="auth_element" style={{ padding: '16px', border: '1px dashed #ccc', marginBottom: '16px' }}>
                    <p className="text-gray-700 font-semibold mb-2">✂️ Crop Family Head Photo:</p>
                    {/* Aspect Ratio Lock Toggle */}
                    <div style={{ marginBottom: '10px' }}>
                      <input
                        type="checkbox"
                        id="aspectRatioLock"
                        checked={isAspectRatioLocked}
                        onChange={() => setIsAspectRatioLocked(!isAspectRatioLocked)}
                      />
                      <label htmlFor="aspectRatioLock" style={{ marginLeft: '8px' }}>
                        {isAspectRatioLocked ? 'Lock Aspect Ratio (Passport size)' : 'Unlock Aspect Ratio (Free Crop)'}
                      </label>
                    </div>
                    {/* Use the responsive container classes from crop.css */}
                    <div className="cropper-container-wrapper">
                      <div className="cropper-inner-container">
                        {imageURL && (
                          <Cropper
                            image={imageURL}
                            crop={crop}
                            zoom={zoom}
                            onCropChange={setCrop}
                            onZoomChange={setZoom}
                            onCropComplete={onCropComplete}

                            aspect={isAspectRatioLocked ? 2.5 / 3 : undefined}
                            cropShape="rect"
                            showGrid={true}

                          />
                        )}
                      </div>
                    </div>
                    <div style={{ marginTop: '20px' }}>
                      <label htmlFor="zoom-range" className="block text-sm font-medium text-gray-700">Zoom:</label>
                      <input
                        id="zoom-range"
                        type="range"
                        min={1}
                        max={10}
                        step={0.1}
                        value={zoom}
                        onChange={(e) => setZoom(parseFloat(e.target.value))}
                        className="w-full mb-4"
                      />
                    </div>
                    <button
                      type="button"
                      onClick={handleCrop}
                      className="action-button"
                    >
                      ✂️ Crop Image
                    </button>
                  </div>
                )}
                {/* Display Cropped Image Preview */}
                {croppedImageBase64 && uploadMode === 'both' && (
                  <>
                    <div className="auth_element">
                      <p className="text-gray-700 font-semibold mb-2">🔍 புகைப்பட முன்னோட்டம்:</p>
                    </div>
                    <img
                      src={croppedImageBase64}
                      alt="புகைப்பட முன்னோட்டம்"
                      style={{ maxWidth: '100%', height: 'auto', objectFit: 'cover', border: '1px solid #ccc', padding: '4px' }}
                    />
                  </>
                )}
                <button type="submit" disabled={!isValid || isSubmitting || !nameData.lastName.trim() || !nameData.firstName.trim()}
                  className={`auth_submit ${!isValid || isSubmitting ? "disabled_btn" : ""}`}
                >
                  {loading ? 'சமர்ப்பிக்கிறது...' : 'சமர்ப்பிக்கவும்'}
                </button>
                <br />
              </div>
            </form>

            <button onClick={() => navigate(`/family/member-create/${familyMemberData.familyMember.familyId}`)}>குடும்ப உறுப்பினரைச் சேர்</button>
            <br />
            <button onClick={() => navigate(`/family/view/${familyMemberData.familyMember.familyId}`)}>குடும்ப விவரங்கள்</button>
          </div>
        </div>
      </div>

      <Footer />
    </>
  );
}

export default FamilyMemberUpdate;
